import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import { Download, Share2, ArrowLeft } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";

const LABELS = {
  logical_reasoning: "Logic", fluid_reasoning: "Fluid", pattern_recognition: "Patterns",
  visual_spatial: "Spatial", working_memory: "Memory", verbal_reasoning: "Verbal",
  quantitative_reasoning: "Quant", processing_efficiency: "Speed", cognitive_flexibility: "Flex",
  emotional_recognition: "Emotion", perspective_taking: "Perspective", social_reasoning: "Social",
  conflict_navigation: "Conflict", communication_judgment: "Comm.", emotional_regulation: "Regulation",
  empathic_reasoning: "Empathy", self_awareness: "Self-Aware",
};

export default function Share() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [card, setCard] = useState(null);
  const [generating, setGenerating] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/assessment/${aid}/share-card`); setCard(data); }
      catch { toast.error("Share card unavailable"); nav("/dashboard"); }
    })();
  }, [aid, nav]);

  const download = async () => {
    if (!ref.current) return;
    setGenerating(true);
    try {
      const dataUrl = await toPng(ref.current, { quality: 1, pixelRatio: 2, backgroundColor: "#070C18" });
      const a = document.createElement("a");
      a.href = dataUrl; a.download = `dualq-${card.archetype.toLowerCase()}.png`; a.click();
      toast.success("Card downloaded");
    } catch { toast.error("Could not generate image"); }
    finally { setGenerating(false); }
  };

  const nativeShare = async () => {
    const text = `My DualQ: ${card.archetype_label}. Discover yours at ${window.location.origin}`;
    try {
      if (navigator.share) await navigator.share({ title: "My DualQ", text, url: window.location.origin });
      else { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    } catch (err) {
      // User-cancelled share sheet throws AbortError — don't surface that.
      if (err?.name !== "AbortError") {
        console.error("Share failed", err);
        toast.error("Could not share. Link copied to clipboard instead.");
        try { await navigator.clipboard.writeText(text); } catch { /* clipboard unavailable */ }
      }
    }
  };

  if (!card) return <div><Nav /><div className="max-w-xl mx-auto px-5 py-20 text-slate-400">Building your card…</div></div>;

  return (
    <div><Nav />
      <div className="max-w-2xl mx-auto px-5 py-8">
        <button onClick={()=>nav(`/results/${aid}`)} className="mb-4 text-sm text-slate-400 hover:text-slate-200 inline-flex items-center gap-1" data-testid="back-to-results">
          <ArrowLeft className="w-4 h-4" /> Back to my DualQ
        </button>

        <div ref={ref} className="rounded-3xl p-8 sm:p-10 bg-gradient-to-br from-[#0E172A] via-[#131E36] to-[#1A2847] border border-[#E0A96D]/25 relative overflow-hidden" data-testid="share-card">
          <div className="absolute -top-20 -right-20 w-[260px] h-[260px] rounded-full bg-[#F59E0B]/15 blur-[90px]" />
          <div className="absolute -bottom-20 -left-20 w-[260px] h-[260px] rounded-full bg-[#38BDF8]/15 blur-[90px]" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#38BDF8] to-[#F59E0B] grid place-items-center text-[#070C18] font-bold text-lg">Q</div>
              <div className="font-serif text-2xl">DualQ</div>
            </div>
            <div className="uppercase font-mono text-[11px] tracking-[0.24em] text-[#E0A96D] mb-2">My DualQ Archetype</div>
            <div className="font-serif text-4xl sm:text-5xl leading-tight mb-5" data-testid="card-archetype">
              The {card.archetype_label.replace(/^The\s+/i, "")}
            </div>
            <div className="grid grid-cols-3 gap-3 mb-5">
              {[["Cognitive", card.cognitive_index, "#38BDF8"],
                ["Emotional", card.emotional_index, "#F59E0B"],
                ["Calibration", card.calibration ?? "—", "#A855F7"]].map(([l,v,c])=>(
                <div key={l} className="rounded-xl bg-[#070C18]/60 p-3 border border-slate-800/80">
                  <div className="text-[10px] uppercase tracking-widest font-mono" style={{color:c}}>{l}</div>
                  <div className="font-serif text-2xl mt-0.5">{v}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="uppercase font-mono text-[10px] text-[#38BDF8] tracking-widest mb-1">Top Cognitive</div>
                <ul className="space-y-1 text-sm">
                  {card.top_cognitive.map(x => (
                    <li key={x.domain} className="flex justify-between">
                      <span className="text-slate-300">{LABELS[x.domain] || x.domain}</span>
                      <span className="font-mono text-slate-100">{x.score}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="uppercase font-mono text-[10px] text-[#F59E0B] tracking-widest mb-1">Top Emotional</div>
                <ul className="space-y-1 text-sm">
                  {card.top_emotional.map(x => (
                    <li key={x.domain} className="flex justify-between">
                      <span className="text-slate-300">{LABELS[x.domain] || x.domain}</span>
                      <span className="font-mono text-slate-100">{x.score}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-slate-800/60 flex items-center justify-between text-[11px] text-slate-500 font-mono">
              <span>Profile confidence · {card.profile_confidence}%</span>
              <span>What's your DualQ?</span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={download} disabled={generating}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B] disabled:opacity-60"
            data-testid="download-card-btn">
            <Download className="w-4 h-4" /> {generating?"Rendering…":"Download image"}
          </button>
          <button onClick={nativeShare} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-700 hover:border-slate-500 text-slate-200" data-testid="native-share-btn">
            <Share2 className="w-4 h-4" /> Share link
          </button>
        </div>
      </div>
    </div>
  );
}
